import { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Badge,
  Spinner,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useColorModeValue,
  useBreakpointValue,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react'
import { Plus, PencilSimple, Trash, ArrowsLeftRight, List } from '@phosphor-icons/react'
import api from '../services/api'
import { useRef } from 'react'
import { Sidebar } from '../layouts/DefaultLayout'
import socketService, { SOCKET_EVENTS } from '../services/socket'

interface Category {
  id: number
  name: string
  chatCount: number
}

export default function Categories() {
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [formData, setFormData] = useState({ name: '' })
  const [transferTargetId, setTransferTargetId] = useState<number | null>(null)
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const menuIconColor = useColorModeValue('gray.700', 'white')

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const { isOpen: isTransferOpen, onOpen: onTransferOpen, onClose: onTransferClose } = useDisclosure()
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const fetchInternalTicketsCount = async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    fetchInternalTicketsCount()
    const interval = setInterval(fetchInternalTicketsCount, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    }
  }, [])

  const fetchCategories = async () => {
    setIsLoading(true)
    try {
      const res = await api.get('/admin/categories')
      setCategories(res.data)
    } catch (error) {
      console.error('Error fetching categories:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  const handleCreate = () => {
    setSelectedCategory(null)
    setFormData({ name: '' })
    onFormOpen()
  }

  const handleEdit = (category: Category) => {
    setSelectedCategory(category)
    setFormData({ name: category.name })
    onFormOpen()
  }

  const handleDelete = (category: Category) => {
    setSelectedCategory(category)
    if (category.chatCount > 0) {
      onTransferOpen()
    } else {
      onDeleteOpen()
    }
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Nome é obrigatório', status: 'warning', duration: 3000 })
      return
    }

    try {
      if (selectedCategory) {
        await api.put(`/admin/categories/${selectedCategory.id}`, formData)
        toast({ title: 'Categoria atualizada', status: 'success', duration: 3000 })
      } else {
        await api.post('/admin/categories', formData)
        toast({ title: 'Categoria criada', status: 'success', duration: 3000 })
      }
      onFormClose()
      fetchCategories()
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedCategory) return

    try {
      await api.delete(`/admin/categories/${selectedCategory.id}`)
      toast({ title: 'Categoria excluída', status: 'success', duration: 3000 })
      onDeleteClose()
      fetchCategories()
    } catch (error: any) {
      toast({
        title: 'Erro ao excluir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  const handleTransfer = async () => {
    if (!selectedCategory || !transferTargetId) return

    try {
      await api.post(`/admin/categories/${selectedCategory.id}/transfer`, {
        targetCategoryId: transferTargetId,
      })
      toast({ title: 'Atendimentos transferidos', status: 'success', duration: 3000 })
      onTransferClose()
      setTransferTargetId(null)
      // Now delete the category
      await api.delete(`/admin/categories/${selectedCategory.id}`)
      toast({ title: 'Categoria excluída', status: 'success', duration: 3000 })
      fetchCategories()
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  return (
    <>
      {/* Mobile menu drawer */}
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} internalTicketsBadge={internalTicketsBadge} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Box p={{ base: 3, md: 6 }} h="100%" overflowY="auto">
        <VStack align="stretch" spacing={{ base: 4, md: 6 }}>
          <HStack justify="space-between" spacing={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
            <HStack spacing={3} align="center" flex={1} minW={0}>
              {isMobile && (
                <IconButton
                  aria-label="Menu"
                  icon={<List size={20} />}
                  variant="ghost"
                  onClick={onMenuOpen}
                  size="sm"
                  color={menuIconColor}
                  _hover={{ bg: borderColor }}
                  flexShrink={0}
                />
              )}
              <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color={textPrimary}>
                Categorias de Atendimento
              </Text>
            </HStack>
            <Button
              leftIcon={<Plus size={18} />}
              colorScheme="yellow"
              onClick={handleCreate}
              size={{ base: 'sm', md: 'md' }}
              flexShrink={0}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Nova Categoria</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Nova</Text>
            </Button>
          </HStack>

        {isLoading ? (
          <Spinner size="xl" color="yellow.500" alignSelf="center" />
        ) : isMobile ? (
          <VStack spacing={3} align="stretch">
            {categories.map((category) => (
              <Box
                key={category.id}
                bg={surfaceBg}
                p={4}
                borderRadius="lg"
                border="1px"
                borderColor={borderColor}
              >
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between" align="start">
                    <Text color={textPrimary} fontWeight="semibold" fontSize="sm" flex={1} minW={0}>
                      {category.name}
                    </Text>
                    <HStack spacing={1} flexShrink={0}>
                      <IconButton
                        aria-label="Editar"
                        icon={<PencilSimple size={16} />}
                        size="xs"
                        variant="ghost"
                        onClick={() => handleEdit(category)}
                      />
                      <IconButton
                        aria-label="Excluir"
                        icon={<Trash size={16} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(category)}
                      />
                    </HStack>
                  </HStack>
                  <HStack justify="space-between" align="center">
                    <Text fontSize="xs" color={textMuted}>
                      Atendimentos:
                    </Text>
                    <Badge colorScheme={category.chatCount > 0 ? 'blue' : 'gray'} fontSize="xs">
                      {category.chatCount}
                    </Badge>
                  </HStack>
                </VStack>
              </Box>
            ))}
          </VStack>
        ) : (
          <Box bg={surfaceBg} borderRadius="xl" overflow="hidden">
            <Table>
              <Thead>
                <Tr>
                  <Th color={textMuted}>Nome</Th>
                  <Th color={textMuted} isNumeric>Atendimentos</Th>
                  <Th color={textMuted} w="100px">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categories.map((category) => (
                  <Tr key={category.id}>
                    <Td>
                      <Text color={textPrimary}>{category.name}</Text>
                    </Td>
                    <Td isNumeric>
                      <Badge colorScheme={category.chatCount > 0 ? 'blue' : 'gray'}>
                        {category.chatCount}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Editar"
                          icon={<PencilSimple size={18} />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(category)}
                        />
                        <IconButton
                          aria-label="Excluir"
                          icon={<Trash size={18} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(category)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>
      </Box>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size={{ base: 'full', sm: 'md' }}>
        <ModalOverlay />
        <ModalContent bg={surfaceBg}>
          <ModalHeader>{selectedCategory ? 'Editar' : 'Nova'} Categoria</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Nome</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ name: e.target.value })}
                placeholder="Ex: Financeiro, Suporte Técnico..."
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Cancelar
            </Button>
            <Button colorScheme="yellow" onClick={handleSubmit}>
              {selectedCategory ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={surfaceBg}>
            <AlertDialogHeader>Excluir Categoria</AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja excluir a categoria "{selectedCategory?.name}"?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Transfer Modal */}
      <Modal isOpen={isTransferOpen} onClose={onTransferClose}>
        <ModalOverlay />
        <ModalContent bg={surfaceBg}>
          <ModalHeader>Transferir Atendimentos</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color={textSecondary}>
                A categoria "{selectedCategory?.name}" possui {selectedCategory?.chatCount} atendimento(s).
                Transfira-os para outra categoria antes de excluir.
              </Text>
              <FormControl>
                <FormLabel>Transferir para</FormLabel>
                <Select
                  placeholder="Selecione uma categoria"
                  value={transferTargetId || ''}
                  onChange={(e) => setTransferTargetId(Number(e.target.value) || null)}
                >
                  {categories
                    .filter((c) => c.id !== selectedCategory?.id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTransferClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="red"
              onClick={handleTransfer}
              isDisabled={!transferTargetId}
              leftIcon={<ArrowsLeftRight size={18} />}
            >
              Transferir e Excluir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
